import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { MedicalRequestStatusLookupCodes } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";

class MedicalRequestStatus {
    public static get TypeCode(): string {
        return "MedicalRequestStatus";
    }

    /**
     * @deprecated Use {@link getDefault} instead. This is only kept for backwards compatibility, since there's no one-to-one equivalent in the newer statuses.
     */
    public static get Pending(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.PENDING);
    }

    /**
     * @deprecated Use {@link ReportReceived} instead. This is only kept for backwards compatibility until the {@link cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements} feature flag is removed.
     */
    public static get ResultReceived(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.RESULTSRECEIVED);
    }

    public static get AppointmentScheduled(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.APPOINTMENTSCHEDULED);
    }

    public static get Cancelled(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.CANCELLED);
    }

    public static get Closed(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.CLOSED);
    }

    public static get Draft(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.DRAFT);
    }

    public static get Investigation(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.INVESTIGATION);
    }

    public static get PatientContactedBy3rdParty(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.PATIENTCONTACTTEDBY3rdPARTY);
    }

    public static get ReceivedBy3rdParty(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.RECEIVEDBY3rdPARTY);
    }

    public static get RejectedBy3rdParty(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.REJECTEDBY3rdPARTY);
    }

    public static get ReportReceived(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.REPORTRECEIVED);
    }

    public static get Sent(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.SENT);
    }

    public static get WaitList(): FlatLookupView {
        return this.getStatusByCode(MedicalRequestStatusLookupCodes.WAITLIST);
    }

    public static getAll(): FlatLookupView[] {
        return cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            ? this.getOrderedStatuses()
            : this.getOrderedLegacyDropdownStatuses();
    }

    public static getComboboxItems(): ComboBoxDropDownItem[] {
        return this.getAll().map(lookup => {
            const comboboxItem = lookup.ToComboboxItem();

            // hide deprecated statuses - this is needed to properly display legacy requests
            if (cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements) {
                comboboxItem.Hidden = lookup.LookupID === MedicalRequestStatus.Pending.LookupID
                    || lookup.LookupID === MedicalRequestStatus.ResultReceived.LookupID;
            }

            return comboboxItem;
        });
    }

    public static getPortfolioCheckboxOptions(): Immutable.List<CheckboxUIModel> {
        const statuses = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            ? this.getOrderedStatuses()
            : this.getOrderedLegacyPortfolioFilterStatuses();

        return statuses
            .map((status, index) => new CheckboxUIModel({
                Id: status.LookupID,
                Name: status.GetValue(),
                Checked: ![this.Closed.LookupID, this.Draft.LookupID, this.Cancelled.LookupID].includes(status.LookupID),
                Position: index
            }))
            .toImmutable();
    }

    public static IsCompleted(lookupId: string): boolean {
        const completedStatus = [this.Cancelled, this.Closed];
        return completedStatus.some(x => x.LookupID === lookupId);
    }

    public static isInPendingStatus(lookupId: string): boolean {
        const pendingStatus = [
            this.Pending, this.ResultReceived, this.ReceivedBy3rdParty, this.PatientContactedBy3rdParty, this.Investigation
        ];
        return pendingStatus.some(x => x.LookupID === lookupId);
    }

    public static getDefault(): FlatLookupView {
        return cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
            ? this.Draft
            : this.Pending;
    }

    private static getOrderedLegacyDropdownStatuses(): FlatLookupView[] {
        return [
            this.Pending,
            this.ResultReceived,
            this.Closed,
            this.Cancelled,
            this.ReceivedBy3rdParty,
            this.Investigation,
            this.PatientContactedBy3rdParty
        ];
    }

    private static getOrderedLegacyPortfolioFilterStatuses(): FlatLookupView[] {
        return [
            this.Pending,
            this.ReceivedBy3rdParty,
            this.PatientContactedBy3rdParty,
            this.Investigation,
            this.ResultReceived,
            this.Closed,
            this.Cancelled
        ];
    }

    private static getOrderedStatuses(): FlatLookupView[] {
        return [
            this.Sent,
            this.ReceivedBy3rdParty,
            this.RejectedBy3rdParty,
            this.WaitList,
            this.PatientContactedBy3rdParty,
            this.AppointmentScheduled,
            this.Investigation,
            this.ReportReceived,
            this.Closed,
            this.Cancelled,
            this.Draft,
            this.Pending,
            this.ResultReceived
        ];
    }

    private static getStatusByCode(code: MedicalRequestStatusLookupCodes): FlatLookupView {
        return cacheStore.FindLookupByLookupCode(`${this.TypeCode}_${code}`, this.TypeCode);
    }
}

export { MedicalRequestStatus };
